import { Price } from '@wix/package-picker-components';
import { Experiments, PlanType, ProductChangeType } from 'enums';
import { observer } from 'mobx-react';
import { AdvancedPlansProductModel } from 'models/AdvancedPlansProductModel';
import { RootStore, withInjectedRootStore } from 'providers';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { taxIncludedTranslationKey } from '../../../../../../constants';
import styles from './ProductPrice.scss';
import { Text } from '@wix/design-system';
import { usePremiumCurrencyFormatter } from '@wix/premium-react-text';

export interface ProductPriceProps {
  product: AdvancedPlansProductModel;
  size: 'small' | 'tiny';
  rootStore: RootStore;
  inline?: boolean;
}

const ProductPriceComp: React.FC<ProductPriceProps> = ({
  product,
  size,
  rootStore,
  inline,
}) => {
  const { t } = useTranslation();
  const {
    appContext: { country, currentPlanDetails, isLimitedTimeDiscount },
    isInSale,
    pricesIncludeTax,
    currentCycle,
    isChangePlan,
    planType,
    experiments,
  } = rootStore.appStore;
  const cycle = currentPlanDetails?.paymentCycle || currentCycle;

  const isCycleSelectorInPlansPage =
    experiments[Experiments.CycleSelectorsInProductsPage] === 'true';

  const unifiedChangePlanPhase1Enabled =
    experiments[Experiments.UnifiedChangePlanPhase1] === 'true';
  const shouldShowPriceByCycle =
    isChangePlan && !unifiedChangePlanPhase1Enabled;

  const [currencyToString] = usePremiumCurrencyFormatter();

  const getFullPrice = () => {
    if (isCycleSelectorInPlansPage) {
      return product.getFullPrice();
    }

    return shouldShowPriceByCycle
      ? product.getFullPriceByCycle(cycle)
      : product.getYearlyFullPrice();
  };

  const getActualPrice = () => {
    if (isCycleSelectorInPlansPage) {
      return product.getActualPriceByCycle(currentCycle);
    }

    if (shouldShowPriceByCycle) {
      return product.getActualPriceByCycle(cycle);
    }

    if (
      isChangePlan &&
      product.getProductChangeType() !== ProductChangeType.Upgrade
    ) {
      return fullPrice;
    }

    return (
      product.getFirstCycleYearlyMonthly() || product.getYearlyActualPrice()
    );
  };

  const fullPrice = getFullPrice();
  const isDayfulFreeProduct = product.getName() === 'Free';

  const _fullPrice = isDayfulFreeProduct ? 0 : fullPrice;
  const _actualPrice = isDayfulFreeProduct ? 0 : getActualPrice();

  const shouldShowSale =
    isChangePlan &&
    unifiedChangePlanPhase1Enabled &&
    product.getProductChangeType() !== ProductChangeType.Upgrade
      ? false
      : isInSale;

  const isProductPurchasable = product.isPurchaseEnabled();

  const isCyclePurchasable = isCycleSelectorInPlansPage && isProductPurchasable;
  const isSaleOrLimitedTimeDiscount =
    shouldShowSale || (isLimitedTimeDiscount && planType === PlanType.Business);
  const shouldShowProductFullPrice =
    isCyclePurchasable ||
    (!isCycleSelectorInPlansPage && isSaleOrLimitedTimeDiscount);

  const nodeSavings = () => {
    const savings = product.getFullTermDisplaySavingsForCurrentCycle?.();
    if (
      isCycleSelectorInPlansPage &&
      _fullPrice !== _actualPrice &&
      savings &&
      savings > 0 &&
      isProductPurchasable
    ) {
      return (
        <Text
          size='small'
          weight='bold'
          dataHook='product-savings-text'
          className={isInSale ? styles.savingsSale : styles.savings}
        >
          {t('premium-purchase-plan.cards.cycle_savings', {
            savingsAmount: currencyToString(savings, {
              hideZeroDecimals: true,
            }),
            cycle: currentCycle,
          })}
        </Text>
      );
    }

    return null;
  };

  return (
    <Price
      size={size}
      price={_actualPrice}
      fullPrice={_fullPrice}
      cycleLabel={`/${t('product_price.month')}`}
      taxIncludedLabel={t(taxIncludedTranslationKey[country])}
      shouldShowTaxIncludedLabel={pricesIncludeTax}
      shouldShowProductFullPrice={shouldShowProductFullPrice}
      inline={inline}
      savings={nodeSavings()}
    />
  );
};

export const ProductPrice = observer(withInjectedRootStore(ProductPriceComp));
